/** "Save current view as preset" dialog (opened from PresetsMenu).
 *
 * Lets the user pick WHICH sections the preset covers (columns / filters /
 * sort — pivot is reserved and never offered), name it, and share it. When a
 * source preset is known and owned by the caller (`updateTarget`), an
 * "Update '<name>'" radio saves over it instead of creating a new preset.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import * as presetsApi from '@/api/presets';
import { presetErrorMessage, type GridPresetDetail } from '@/api/presets';
import { useSnackbar } from '@/components/Snackbar';
import type { PresetsToolbarConfig } from '@/hooks/useGridPresets';

const Schema = z
  .object({
    saveMode: z.enum(['new', 'update']),
    name: z.string().trim().max(120),
    visibility: z.enum(['private', 'shared']),
    columns: z.boolean(),
    filters: z.boolean(),
    sort: z.boolean(),
  })
  .refine((v) => v.columns || v.filters || v.sort, {
    message: 'Pick at least one section to save.',
    path: ['columns'],
  })
  .refine((v) => v.saveMode === 'update' || v.name.length > 0, {
    message: 'Name is required.',
    path: ['name'],
  });
type Input = z.infer<typeof Schema>;

interface Props {
  open: boolean;
  onClose: () => void;
  presets: PresetsToolbarConfig;
}

export function SavePresetDialog({ open, onClose, presets }: Props) {
  const snackbar = useSnackbar();
  const { supports, updateTarget } = presets;

  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<Input>({
    resolver: zodResolver(Schema),
    defaultValues: {
      saveMode: 'new',
      name: '',
      visibility: 'private',
      columns: supports.columns,
      filters: supports.filters,
      sort: supports.sort,
    },
  });
  const saveMode = watch('saveMode');

  // Re-seed on open so a reopened dialog reflects the current context.
  useEffect(() => {
    if (open) {
      reset({
        saveMode: 'new',
        name: '',
        visibility: 'private',
        columns: supports.columns,
        filters: supports.filters,
        sort: supports.sort,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const mutation = useMutation({
    mutationFn: (input: Input): Promise<GridPresetDetail> => {
      const settings = presets.capture({
        columns: input.columns,
        filters: input.filters,
        sort: input.sort,
      });
      if (input.saveMode === 'update' && updateTarget) {
        return presetsApi.update(updateTarget.id, { settings });
      }
      return presetsApi.create({
        grid: presets.grid,
        name: input.name,
        visibility: input.visibility,
        settings,
      });
    },
    onSuccess: (preset) => {
      snackbar.show(`Preset '${preset.name}' saved.`, 'success');
      presets.onSaved(preset);
      onClose();
    },
    onError: (e: unknown) => {
      setError('name', { type: 'server', message: presetErrorMessage(e) });
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Save current view as preset</DialogTitle>
      <form onSubmit={handleSubmit((input) => mutation.mutate(input))} noValidate>
        <DialogContent>
          <Stack spacing={2}>
            {updateTarget && (
              <Controller
                name="saveMode"
                control={control}
                render={({ field }) => (
                  <FormControl>
                    <RadioGroup {...field}>
                      <FormControlLabel
                        value="update"
                        control={<Radio size="small" />}
                        label={`Update '${updateTarget.name}'`}
                      />
                      <FormControlLabel
                        value="new"
                        control={<Radio size="small" />}
                        label="Save as a new preset"
                      />
                    </RadioGroup>
                  </FormControl>
                )}
              />
            )}
            {saveMode === 'new' && (
              <>
                <TextField
                  label="Preset name"
                  size="small"
                  autoFocus
                  fullWidth
                  {...register('name')}
                  error={Boolean(errors.name)}
                  helperText={errors.name?.message}
                />
                <Controller
                  name="visibility"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      size="small"
                      label="Visibility"
                      helperText={
                        field.value === 'shared'
                          ? 'Visible to all console users.'
                          : 'Only you can see this preset.'
                      }
                    >
                      <MenuItem value="private">Private</MenuItem>
                      <MenuItem value="shared">Shared</MenuItem>
                    </TextField>
                  )}
                />
              </>
            )}
            {saveMode === 'update' && errors.name && (
              <FormHelperText error>{errors.name.message}</FormHelperText>
            )}
            <FormControl error={Boolean(errors.columns)}>
              <FormLabel sx={{ fontSize: 13 }}>What to save</FormLabel>
              <FormGroup>
                {(
                  [
                    ['columns', 'Columns & order'],
                    ['filters', 'Filters'],
                    ['sort', 'Sort order'],
                  ] as const
                ).map(([key, label]) => (
                  <Controller
                    key={key}
                    name={key}
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={field.value}
                            onChange={field.onChange}
                            disabled={!supports[key]}
                          />
                        }
                        label={label}
                      />
                    )}
                  />
                ))}
              </FormGroup>
              {errors.columns && <FormHelperText>{errors.columns.message}</FormHelperText>}
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={isSubmitting || mutation.isPending}>
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
